import React, { useState, useEffect, useRef } from 'react';
import {
  Box,
  Typography,
  Paper,
  Grid,
  List,
  ListItem,
  ListItemText,
  ListItemAvatar,
  Avatar,
  TextField,
  Button,
  IconButton,
  Popover,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from '@mui/material';
import {
  Send,
  Chat as ChatIcon,
  Person,
  School,
  AdminPanelSettings,
  EmojiEmotions,
} from '@mui/icons-material';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import { useAuth } from '../contexts/AuthContext';
import { useSnackbar } from 'notistack';
import api from '../services/api';

interface Conversation {
  id: number;
  conversation_id?: number; // For backward compatibility
  title: string;
  created_at: string;
  other_user_name: string;
  other_user_role: string;
  last_message?: string;
  last_message_time?: string;
}

interface Message {
  id: number;
  message: string;
  full_name: string;
  role: string;
  created_at: string;
}

interface User {
  id: number;
  full_name: string;
  username: string;
  role: string;
}

const Chat: React.FC = () => {
  const [selectedConversation, setSelectedConversation] = useState<Conversation | null>(null);
  const [newMessage, setNewMessage] = useState('');
  const [openNewChat, setOpenNewChat] = useState(false);
  const [selectedUsers, setSelectedUsers] = useState<number[]>([]);
  const [chatTitle, setChatTitle] = useState('');
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const [emojiAnchorEl, setEmojiAnchorEl] = useState<HTMLElement | null>(null);
  const { user } = useAuth();
  const { enqueueSnackbar } = useSnackbar();
  const queryClient = useQueryClient();

  const EMOJIS = [
    '😀','😁','😂','🤣','😊','😍','😘','😎','🙂','🤗','🤔','😐','🙄','😴','🤤','😷','🤒','🤕','🤢','🤮',
    '🤧','🥳','🤯','😬','😇','😜','🤪','😤','😭','😡','👍','👎','👏','🙏','👌','🤝','💪','🔥','✨','💯',
    '🎉','🥰','😺','😹','😻','🙈','🙉','🙊','🫡','🫶','❤️','💙','💚','💛','💜','🖤'
  ];

  const { data: conversations = [] } = useQuery<Conversation[]>('chat-conversations', async () => {
    const response = await api.get('/chat/conversations');
    return response.data.conversations;
  });

  const { data: messages = [] } = useQuery<Message[]>(
    ['chat-messages', selectedConversation?.id],
    async () => {
      if (!selectedConversation) return [];
      const response = await api.get(`/chat/conversations/${selectedConversation.id}/messages`);
      return response.data.messages;
    },
    {
      enabled: !!selectedConversation,
      refetchInterval: 3000, // Poll every 3 seconds
    }
  );

  const { data: availableUsers = [] } = useQuery<User[]>('chat-users', async () => {
    const response = await api.get('/chat/users');
    return response.data.users;
  });

  const sendMessageMutation = useMutation(
    async (message: string) => {
      await api.post(`/chat/conversations/${selectedConversation!.id}/messages`, { message });
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['chat-messages', selectedConversation?.id]);
        setNewMessage('');
      },
      onError: (error: any) => {
        enqueueSnackbar(error.response?.data?.message || 'Failed to send message', { variant: 'error' });
      },
    }
  );

  const createConversationMutation = useMutation(
    async (conversationData: { title: string; participant_ids: number[] }) => {
      const response = await api.post('/chat/conversations', conversationData);
      return response.data as { conversationId: number };
    },
    {
      onSuccess: async (data) => {
        await queryClient.invalidateQueries('chat-conversations');
        setOpenNewChat(false);
        setSelectedUsers([]);
        setChatTitle('');
        // Auto-select the created conversation
        const newConvId = data.conversationId;
        const updated = await queryClient.fetchQuery('chat-conversations', async () => {
          const res = await api.get('/chat/conversations');
          return res.data.conversations as Conversation[];
        });
        const created = (updated as Conversation[]).find(c => c.id === newConvId || (c as any).conversation_id === newConvId);
        if (created) setSelectedConversation(created);
        enqueueSnackbar('Conversation created successfully!', { variant: 'success' });
      },
      onError: (error: any) => {
        enqueueSnackbar(error.response?.data?.message || 'Failed to create conversation', { variant: 'error' });
      },
    }
  );

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const handleSendMessage = () => {
    if (newMessage.trim() && selectedConversation) {
      sendMessageMutation.mutate(newMessage);
    }
  };

  const toggleEmojiPicker = (e: React.MouseEvent<HTMLElement>) => {
    setEmojiAnchorEl(emojiAnchorEl ? null : e.currentTarget);
  };

  const onPickEmoji = (emoji: string) => {
    setNewMessage(prev => prev + emoji);
  };

  const handleCreateConversation = () => {
    if (chatTitle && selectedUsers.length > 0) {
      createConversationMutation.mutate({
        title: chatTitle,
        participant_ids: selectedUsers,
      });
    }
  };

  const getRoleIcon = (role: string) => {
    switch (role) {
      case 'admin':
        return <AdminPanelSettings />;
      case 'supervisor':
        return <School />;
      case 'student':
        return <Person />;
      default:
        return <Person />;
    }
  };

  const getRoleColor = (role: string) => {
    switch (role) {
      case 'admin':
        return 'error';
      case 'supervisor':
        return 'warning';
      case 'student':
        return 'info';
      default:
        return 'default';
    }
  };

  return (
    <Box sx={{ width: '100%' }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4">Chat</Typography>
        <Button
          variant="contained"
          startIcon={<ChatIcon />}
          onClick={() => setOpenNewChat(true)}
        >
          New Conversation
        </Button>
      </Box>

      <Grid container spacing={3} sx={{ height: '70vh' }}>
        {/* Available Users List (left) */}
        <Grid item xs={12} md={4}>
          <Paper sx={{ height: '100%', overflow: 'auto' }}>
            <List>
              {availableUsers.map((u) => (
                <ListItem
                  key={u.id}
                  button
                  onClick={() => {
                    // Find existing conversation with this user if present
                    const existing = conversations.find(c => (c as any).other_user_id === u.id);
                    if (existing) {
                      setSelectedConversation(existing);
                    } else {
                      setSelectedUsers([u.id]);
                      setChatTitle(`Chat with ${u.full_name}`);
                      setOpenNewChat(true);
                    }
                  }}
                >
                  <ListItemAvatar>
                    <Avatar>
                      {getRoleIcon(u.role)}
                    </Avatar>
                  </ListItemAvatar>
                  <ListItemText
                    primary={u.full_name}
                    secondary={
                      <Typography variant="body2" color="text.secondary">
                        {u.username}
                      </Typography>
                    }
                  />
                  <Chip
                    label={u.role}
                    color={getRoleColor(u.role) as any}
                    size="small"
                  />
                </ListItem>
              ))}
            </List>
          </Paper>
        </Grid>

        {/* Messages / Conversations (right) */}
        <Grid item xs={12} md={8}>
          <Paper sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
            {selectedConversation ? (
              <>
                {/* Chat Header */}
                <Box sx={{ p: 2, borderBottom: 1, borderColor: 'divider' }}>
                  <Typography variant="h6">{selectedConversation.title}</Typography>
                  <Typography variant="body2" color="text.secondary">
                    {selectedConversation.other_user_name} • {selectedConversation.other_user_role}
                  </Typography>
                </Box>

                {/* Messages */}
                <Box sx={{ flexGrow: 1, overflow: 'auto', p: 2 }}>
                  {messages.map((message) => (
                    <Box
                      key={message.id}
                      sx={{
                        display: 'flex',
                        justifyContent: message.full_name === user?.full_name ? 'flex-end' : 'flex-start',
                        mb: 2,
                      }}
                    >
                      <Paper
                        sx={{
                          p: 2,
                          maxWidth: '70%',
                          backgroundColor: message.full_name === user?.full_name ? 'primary.main' : 'background.paper',
                          color: message.full_name === user?.full_name ? 'white' : 'text.primary',
                          border: 1,
                          borderColor: 'divider',
                        }}
                      >
                        <Typography variant="body2" sx={{ mb: 1, color: message.full_name === user?.full_name ? 'white' : 'text.secondary' }}>
                          {message.full_name}
                        </Typography>
                        <Typography variant="body1" sx={{ color: message.full_name === user?.full_name ? 'white' : 'text.primary' }}>
                          {message.message}
                        </Typography>
                        <Typography variant="caption" sx={{ 
                          opacity: 0.7,
                          color: message.full_name === user?.full_name ? 'white' : 'text.secondary'
                        }}>
                          {new Date(message.created_at).toLocaleTimeString()}
                        </Typography>
                      </Paper>
                    </Box>
                  ))}
                  <div ref={messagesEndRef} />
                </Box>

                {/* Message Input */}
                <Box sx={{ p: 2, borderTop: 1, borderColor: 'divider' }}>
                  <Box sx={{ display: 'flex', gap: 1 }}>
                    <IconButton aria-label="emoji" onClick={toggleEmojiPicker} size="large">
                      <EmojiEmotions />
                    </IconButton>
                    <TextField
                      fullWidth
                      placeholder="Type a message..."
                      value={newMessage}
                      onChange={(e) => setNewMessage(e.target.value)}
                      onKeyPress={(e) => e.key === 'Enter' && handleSendMessage()}
                    />
                    <Button
                      variant="contained"
                      onClick={handleSendMessage}
                      disabled={!newMessage.trim()}
                    >
                      <Send />
                    </Button>
                  </Box>
                  <Popover
                    open={Boolean(emojiAnchorEl)}
                    anchorEl={emojiAnchorEl}
                    onClose={() => setEmojiAnchorEl(null)}
                    anchorOrigin={{ vertical: 'top', horizontal: 'left' }}
                  >
                    <Box sx={{ p: 1, maxWidth: 260 }}>
                      <Grid container spacing={1}>
                        {EMOJIS.map((e) => (
                          <Grid item key={e}>
                            <Button onClick={() => onPickEmoji(e)} size="small" sx={{ minWidth: 32, p: 0 }}>
                              {e}
                            </Button>
                          </Grid>
                        ))}
                      </Grid>
                    </Box>
                  </Popover>
                </Box>
              </>
            ) : (
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%' }}>
                <Typography variant="h6" color="text.secondary">
                  Select a conversation or pick a user on the left to start chatting
                </Typography>
              </Box>
            )}
          </Paper>
        </Grid>
      </Grid>

      {/* New Conversation Dialog */}
      <Dialog open={openNewChat} onClose={() => setOpenNewChat(false)} maxWidth="sm" fullWidth>
        <DialogTitle>New Conversation</DialogTitle>
        <DialogContent>
          <TextField
            fullWidth
            label="Conversation Title"
            value={chatTitle}
            onChange={(e) => setChatTitle(e.target.value)}
            sx={{ mb: 2, mt: 1 }}
          />
          <FormControl fullWidth>
            <InputLabel>Select Participants</InputLabel>
            <Select
              multiple
              value={selectedUsers}
              onChange={(e) => setSelectedUsers(e.target.value as number[])}
              label="Select Participants"
            >
              {availableUsers.map((user) => (
                <MenuItem key={user.id} value={user.id}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Avatar sx={{ width: 24, height: 24 }}>
                      {getRoleIcon(user.role)}
                    </Avatar>
                    {user.full_name} ({user.role})
                  </Box>
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenNewChat(false)}>Cancel</Button>
          <Button
            variant="contained"
            onClick={handleCreateConversation}
            disabled={!chatTitle || selectedUsers.length === 0}
          >
            Create
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default Chat; 